import { Col, Row } from "antd";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { Booking } from "../../../../../../../types/bookings";
import { SelectOption } from "../../../../../../../types/select";
import { AppButton } from "../../../../../../atoms/Button";
import { DateField, InputField, ReactSelectField } from "../../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../../atoms/FormElement/InputNumberField";
import { getInputPlaceholderMessage, getValidationProps } from "../../../../../../libs/form";
import { convertToDayjs, DecimalUtils } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { useTorRules } from "../../../../hooks/useTorRules";
import { no_of_installments } from "../../../utils";
import { rentTypeOptions } from "../../AddPlanComm";
import { EditableField } from "../../EditableField";
import { TOR_PERIOD_OPTIONS } from "../constants";
import type { EscalateRentController } from "./useEscalateRentAfterCopy";
import type { PlanFieldItem } from "./YearCard";

type RentTypeValue = "areaSqft" | "fixed";

type PaymentPlanFieldsProps = {
  planIndex: number;
  item: PlanFieldItem;
  hasValidTorType: boolean;
  isPendingGenerateScheduleBreakdown: boolean;
  escalateRentController: EscalateRentController;
  onGenerateBreakdown: () => void;
};

export const PaymentPlanFields = (props: PaymentPlanFieldsProps) => {
  const {
    planIndex,
    item,
    hasValidTorType,
    isPendingGenerateScheduleBreakdown,
    escalateRentController,
    onGenerateBreakdown,
  } = props;

  const { isPaymentPlanApprovalSent, propertyDetails } = useBookingSelector();
  const { isTurnoverRentOnly } = useTorRules();

  const { control, formState, setValue, getValues, clearErrors } = useFormContext();
  const errors = formState?.errors?.plans?.[planIndex];

  const baseFieldName = `plans[${planIndex}]`;

  const plan = useWatch({ control, name: baseFieldName });
  const yearNumber = planIndex + 1;

  const selectedRentType: SelectOption<RentTypeValue> = plan?.rentType ?? getValues(`${baseFieldName}.rentType`);
  const isRentTypeSelected = Boolean(selectedRentType?.value);
  const isPerSqFt = selectedRentType?.value === "areaSqft";

  const { bookingDetails } = useSelector((state: any) => state?.newBooking);
  const { leaseTerms } = (bookingDetails ?? {}) as Booking;

  const {
    isEscalateAfterCopy,
    isRentAmountDisabled,
    handleEscalateRentPercentChange,
    handleRentTypeChangeWithEscalationReset,
  } = escalateRentController;

  useEffect(() => {
    if (!isTurnoverRentOnly) return;

    setValue(`${baseFieldName}.installments`, null, { shouldValidate: false, shouldDirty: false });
    clearErrors(`${baseFieldName}.installments`);
  }, [isTurnoverRentOnly, baseFieldName, setValue, clearErrors]);

  const rentValueDisabled = !isRentTypeSelected || isRentAmountDisabled;
  const annualRentDisabled =
    isTurnoverRentOnly || !isRentTypeSelected || selectedRentType?.value === "areaSqft" || isRentAmountDisabled;

  return (
    <>
      <Row gutter={10}>
        <InputField
          classes="fm-input-hidden"
          fieldname={`${baseFieldName}.noOfYear`}
          control={control}
          initValue={yearNumber}
          iProps={{ readOnly: true, type: "hidden" }}
        />
        <Col span={5}>
          <EditableField
            label="No. of Installments"
            isReadOnly={isPaymentPlanApprovalSent}
            oldValue={item.oldInstallments}
            newValue={item.installments}
          >
            <ReactSelectField
              label="No. of Installments"
              fieldname={`${baseFieldName}.installments`}
              control={control}
              isDisabled={isTurnoverRentOnly}
              iProps={{
                placeholder: getInputPlaceholderMessage("no. of payments"),
                isDisabled: isTurnoverRentOnly,
              }}
              selectOption={no_of_installments}
              {...getValidationProps({ fieldName: "installments", errors, isRequired: !isTurnoverRentOnly })}
            />
          </EditableField>
        </Col>
        <Col span={5}>
          <EditableField
            label="Rent type"
            isReadOnly={isPaymentPlanApprovalSent}
            oldValue={item.oldRentType}
            newValue={item.rentType}
            options={rentTypeOptions}
          >
            <ReactSelectField
              label="Rent type"
              fieldname={`${baseFieldName}.rentType`}
              initValue={item.rentType}
              control={control}
              onChange={handleRentTypeChangeWithEscalationReset}
              iProps={{
                placeholder: getInputPlaceholderMessage("rent type"),
                isDisabled: isTurnoverRentOnly,
              }}
              selectOption={rentTypeOptions}
              {...getValidationProps({ fieldName: "rentType", errors, isRequired: true })}
            />
          </EditableField>
        </Col>
        {isEscalateAfterCopy && (
          <Col span={3}>
            <InputNumberField
              label="Escalate rent (%)"
              fieldname={`${baseFieldName}.escalateRentPercent`}
              control={control}
              defaultValue={item.escalateRentPercent ?? 0}
              onChange={handleEscalateRentPercentChange}
              inputNumberProps={{
                placeholder: getInputPlaceholderMessage("e.g. 5"),
                min: 0,
                max: 100,
              }}
              format="currency"
            />
          </Col>
        )}
        {isPerSqFt && (
          <>
            <Col span={4}>
              <EditableField
                label="Unit Area (Sq.Ft)"
                isReadOnly={isPaymentPlanApprovalSent}
                oldValue={item.oldUnitAreaInSqft}
                newValue={item.unitAreaInSqft}
              >
                <InputNumberField
                  control={control}
                  label="Unit Area (Sq.Ft)"
                  fieldname={`${baseFieldName}.unitAreaInSqft`}
                  defaultValue={
                    item.unitAreaInSqft ?? (propertyDetails?.areaSqft ? Number(propertyDetails?.areaSqft) : 1)
                  }
                  inputNumberProps={{ precision: 2 }}
                  disabled
                />
              </EditableField>
            </Col>
            <Col flex="1">
              <EditableField
                label="Rent Value"
                isReadOnly={isPaymentPlanApprovalSent}
                oldValue={item.oldRentValue}
                newValue={item.rentValue}
              >
                <InputNumberField
                  label="Rent Value"
                  fieldname={`${baseFieldName}.rentValue`}
                  control={control}
                  defaultValue={item.rentValue}
                  onChange={(value) => {
                    if (!isPerSqFt || isEscalateAfterCopy) return;
                    const area = getValues(`${baseFieldName}.unitAreaInSqft`) ?? 0;
                    if (!area) return;
                    setValue(
                      `${baseFieldName}.annualRent`,
                      String(DecimalUtils.toNumber(DecimalUtils.multiply(value ?? 0, area))),
                    );
                  }}
                  disabled={rentValueDisabled}
                  inputNumberProps={{ placeholder: getInputPlaceholderMessage("rent value"), min: 1 }}
                  format="currency"
                  {...getValidationProps({ fieldName: "rentValue", errors, isRequired: true })}
                />
              </EditableField>
            </Col>
          </>
        )}
        <Col span={5}>
          <EditableField
            label="Rent"
            isReadOnly={isPaymentPlanApprovalSent}
            oldValue={item.oldAnnualRent}
            newValue={item.annualRent}
          >
            <InputNumberField
              label="Rent"
              fieldname={`${baseFieldName}.annualRent`}
              inputNumberProps={{
                placeholder:
                  selectedRentType?.value === "areaSqft"
                    ? getInputPlaceholderMessage("rent value to calculate rent")
                    : getInputPlaceholderMessage("rent"),
              }}
              disabled={annualRentDisabled}
              control={control}
              defaultValue={item.annualRent}
              format="currency"
              {...getValidationProps({
                fieldName: "annualRent",
                errors,
                isRequired: true,
                rules: {
                  min: isTurnoverRentOnly
                    ? { value: 0, message: "Rent must be greater or equal to 0" }
                    : { value: 1, message: "Rent must be greater than 0" },
                },
              })}
            />
          </EditableField>
        </Col>
      </Row>
      <Row gutter={10}>
        {hasValidTorType && (
          <Col span={5}>
            <EditableField
              label="No. of TOR period"
              isReadOnly={isPaymentPlanApprovalSent}
              oldValue={item.oldInstallments}
              newValue={item.installments}
            >
              <ReactSelectField
                label="No. of TOR period"
                fieldname={`${baseFieldName}.torPeriod`}
                control={control}
                iProps={{ placeholder: getInputPlaceholderMessage("no. of TOR period") }}
                selectOption={TOR_PERIOD_OPTIONS}
                {...getValidationProps({ fieldName: "torPeriod", errors, isRequired: true })}
              />
            </EditableField>
          </Col>
        )}
        {planIndex < 1 && (
          <Col span={5}>
            <EditableField
              label="Security Deposit"
              isReadOnly={isPaymentPlanApprovalSent}
              oldValue={item.oldSecurityDeposit}
              newValue={item.securityDeposit}
            >
              <InputNumberField
                label="Security Deposit"
                fieldname={`${baseFieldName}.securityDeposit`}
                inputNumberProps={{
                  placeholder: getInputPlaceholderMessage("security deposit"),
                }}
                control={control}
                defaultValue={item.securityDeposit}
                format="currency"
                {...getValidationProps({
                  fieldName: "securityDeposit",
                  errors,
                  isRequired: true,
                  rules: { min: { value: 0, message: "Security deposit must be greater or equal to 0" } },
                })}
              />
            </EditableField>
          </Col>
        )}
        {planIndex < 1 && (
          <Col span={4}>
            <DateField
              label="Rent Commencement Date"
              fieldname={`${baseFieldName}.rentCommencementDate`}
              initValue={convertToDayjs(leaseTerms?.commencementDate)}
              disabled
            />
          </Col>
        )}
        {!isPaymentPlanApprovalSent && (
          <Col style={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <AppButton variant="subtle" loading={isPendingGenerateScheduleBreakdown} onClick={onGenerateBreakdown}>
              Generate Breakdown
            </AppButton>
          </Col>
        )}
      </Row>
    </>
  );
};
